import styled from '@emotion/styled'
import { css } from '@emotion/react'

import media from '../../styles/media'
import { colors } from '../../styles/theme'
import { bgGradientAngle, bgGradientSize } from '../../styles/keyframes'

const S = {}

S.ContentModule = styled.section`
  &.storybook-preview {
    h1 {
      color: white;
    }

    p {
      color: ${colors.silver};
    }

    img {
      ${media.md`
        width: 82.962963%;
        height: auto;
      `};
    }
  }

  position: relative;
  background-color: ${({ bgColor }) => bgColor && bgColor};
  overflow: ${({ bgGradientIsAnimated }) => bgGradientIsAnimated && 'hidden'};

  .wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 72.5rem;
    margin-left: auto;
    margin-right: auto;
    padding-left: 1.25rem;
    padding-right: 1.25rem;
    z-index: 2;

    ${media.sm`
      padding-left: 2.5rem;
      padding-right: 2.5rem;
    `};

    ${media.md`
      justify-content: space-between;
    `};

    ${({ isBasicLayout }) =>
      isBasicLayout &&
      css`
        padding-top: 2rem;
        padding-bottom: 3rem;

        ${media.sm`
          padding-top: 4.5rem;
          padding-bottom: 4.5rem;
        `};

        ${media.lg`
          padding-top: 6rem;
          padding-bottom: 6rem;
        `};
      `};

    ${({ hasImg }) => css`
      ${media.md`
        flex-direction: ${hasImg ? 'row' : 'column'};
      `};
    `};
  }

  > .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }

  .bg-img {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-repeat: no-repeat;
    background-image: ${({ bgImgSrc }) => bgImgSrc && `url(${bgImgSrc})`};
    background-position: ${({ bgImgPosition }) =>
      bgImgPosition && bgImgPosition};
    background-size: ${({ bgImgSize }) => bgImgSize && bgImgSize};

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      position: relative;
      z-index: 1;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${({
        bgImgHasOverlay,
        bgImgOverlayRgb,
        bgImgOverlayOpacity,
      }) =>
        bgImgHasOverlay && `rgba(${bgImgOverlayRgb}, ${bgImgOverlayOpacity})`};
    }
  }

  .cr-logo {
    width: 13.125rem;
    height: auto;
    margin-bottom: 1.5rem;

    ${media.sm`
      width: 18.75rem;
      margin-bottom: 2.5rem;
    `};

    path {
      fill: ${colors.orange};
    }
  }

  .content {
    position: relative;

    ${({ imgStack, imgAlign }) =>
      imgStack === 'bottom' &&
      imgAlign === 'left' &&
      css`
        ${media.md`
          order: 1;
        `};
      `};

    ${({ imgStack, imgAlign }) =>
      imgStack === 'top' &&
      imgAlign === 'left' &&
      css`
        order: 1;
      `};

    ${({ imgStack, imgAlign }) =>
      imgStack === 'top' &&
      imgAlign === 'right' &&
      css`
        order: 1;

        ${media.md`
          order: unset;
        `};
      `};

    ${({ hasImg, imgOnDesktopOnly }) =>
      imgOnDesktopOnly
        ? css`
            ${media.lg`
              width: ${hasImg && '50%'};
            `};
          `
        : css`
            ${media.md`
              width: ${hasImg ? '50%' : '100%'};
            `};
          `};
  }

  .info {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    text-align: center;
    align-items: center;

    ${({ textAlign }) =>
      textAlign === 'left' &&
      css`
        ${media.md`
          align-items: flex-start;

          html:not([dir]) &,
          [dir='ltr'] & {
            text-align: left;
          }

          [dir='rtl'] & {
            text-align: right;
          }
        `};
      `};

    ${({ textAlign }) =>
      textAlign === 'right' &&
      css`
        ${media.md`
          align-items: flex-end;

          html:not([dir]) &,
          [dir='ltr'] & {
            text-align: right;
          }

          [dir='rtl'] & {
            text-align: left;
          }
        `};
      `};

    ${({ textAlign }) =>
      textAlign === 'center' &&
      css`
        ${media.md`
          align-items: center;
          text-align: center;
        `};
      `};

    ${media.md`
      justify-content: center;
      min-height: 25rem;
      padding-top: 2.5rem;
      padding-bottom: 2.5rem;
    `};
  }

  .heading {
    margin-top: 0;
    margin-bottom: 0;
  }

  p {
    margin-top: 0.75rem;
    margin-bottom: 0;
    word-spacing: 0.01875rem;

    + p {
      margin-top: var(--line-height);
    }
  }

  .cta-button-group {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 1.5rem;

    ${media.sm`
      margin-top: 2rem;
      flex-direction: row;
      width: auto;
    `};

    ${media.md`
      margin-top: 2.5rem;
    `};

    > *:not(:only-child):last-of-type {
      margin-block-start: 0.75rem;

      ${media.sm`
        margin-block-start: 0;
        margin-inline-start: 0.625rem;
      `};
    }

    .cta > * {
      word-spacing: 0.175rem;
    }
  }
`

S.GradientBg = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  ${({ bgGradient, bgGradientIsCircle }) =>
    bgGradient &&
    bgGradientIsCircle &&
    css`
      background-image: radial-gradient(${bgGradient});
    `};

  ${({ bgGradient, bgGradientIsCircle }) =>
    bgGradient &&
    !bgGradientIsCircle &&
    css`
      background-image: linear-gradient(${bgGradient});
    `};

  ${({
    bgGradientIsAnimated,
    bgGradientAnimationBgSize,
    bgGradientAnimationDuration,
    bgGradientAnimationDirection,
    bgGradientAnimationTiming,
    bgGradientAnimationName,
    bgGradientAnimationKeyframes,
    bgGradientIsCircle,
  }) =>
    bgGradientIsAnimated &&
    css`
      background-size: ${bgGradientAnimationBgSize};
      animation: ${bgGradientAnimationDuration}s ${bgGradientAnimationTiming}
        infinite ${bgGradientAnimationDirection};
      background-position: ${bgGradientIsCircle && 'center center'};
      animation-name: ${!bgGradientAnimationKeyframes &&
      (bgGradientAnimationName === 'size' ? bgGradientSize : bgGradientAngle)};

      ${bgGradientAnimationKeyframes &&
      `
        animation-name: gradient-animation;
        @keyframes gradient-animation {
          ${bgGradientAnimationKeyframes};
        }
      `};

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `};
`

S.Image = styled.div`
  justify-content: center;
  position: relative;

  ${media.md`
    width: calc(50% - 2.5rem);
    justify-content: flex-end;
    align-items: center;
  `};

  ${({ imgOnDesktopOnly }) =>
    imgOnDesktopOnly
      ? css`
          display: none;

          ${media.lg`
            display: flex;
          `};
        `
      : css`
          display: flex;
        `};

  ${({ imgStack }) =>
    imgStack === 'top' &&
    css`
      margin-bottom: 1.5rem;

      ${media.sm`
        margin-bottom: 2.5rem;
      `};

      ${media.md`
        margin-bottom: 0;
      `};
    `};

  ${({ imgStack }) =>
    imgStack === 'bottom' &&
    css`
      margin-top: 1.5rem;

      ${media.sm`
        margin-top: 2.5rem;
      `};

      ${media.md`
        margin-top: 0;
      `};
    `};

  > * {
    width: 100%;
    height: 100%;

    ${({ imgStack }) =>
      imgStack === 'top' &&
      css`
        width: auto;
        max-height: 17rem;

        ${media.md`
          width: 100%;
          max-height: 100%;
        `};
      `};
  }

  .gatsby-image-wrapper {
    width: 100%;

    img {
      object-fit: contain !important;
    }
  }
`

export default S
